/**
 * Admin Invites API client — invite-only registration management.
 * Calls GET /admin/applications, POST /admin/applications/:id/approve,
 * POST /admin/applications/:id/reject, GET /admin/invite-codes,
 * POST /admin/invite-codes.
 */

import { apiFetch } from "./client";

// ─── Types ───

export type ApplicationStatus = "pending" | "approved" | "rejected";

export type AdminApplication = {
  readonly id: string;
  readonly email: string;
  readonly name: string;
  readonly role: string;
  readonly reason: string;
  readonly status: ApplicationStatus;
  readonly rejection_reason: string | null;
  readonly created_at: string;
};

export type InviteCodeStatus = "available" | "claimed" | "expired";

export type AdminInviteCode = {
  readonly id: string;
  readonly code: string;
  readonly issued_by: string | null;
  readonly issued_by_name: string | null;
  readonly claimed_by: string | null;
  readonly claimed_by_name: string | null;
  readonly status: InviteCodeStatus;
  readonly created_at: string;
  readonly expires_at: string | null;
};

type ListApplicationsParams = {
  readonly status?: ApplicationStatus;
  readonly cursor?: string;
  readonly limit?: number;
};

type ListApplicationsResponse = {
  readonly items: AdminApplication[];
  readonly total: number;
  readonly next_cursor: string | null;
};

type RawApplication = {
  readonly id: string;
  readonly email: string;
  readonly name?: string | null;
  readonly displayName?: string | null;
  readonly role: string;
  readonly reason?: string | null;
  readonly status: string;
  readonly rejection_reason?: string | null;
  readonly rejectionReason?: string | null;
  readonly created_at?: string;
  readonly createdAt?: string;
};

type ListInviteCodesParams = {
  readonly issued_by?: string;
  readonly status?: InviteCodeStatus;
  readonly cursor?: string;
  readonly limit?: number;
};

type ListInviteCodesResponse = {
  readonly items: AdminInviteCode[];
  readonly total: number;
  readonly next_cursor: string | null;
};

type RawInviteCode = {
  readonly id: string;
  readonly code: string;
  readonly issued_by?: string | null;
  readonly issuedBy?: string | null;
  readonly issued_by_name?: string | null;
  readonly claimed_by?: string | null;
  readonly claimedBy?: string | null;
  readonly claimed_by_name?: string | null;
  readonly status?: string;
  readonly created_at?: string;
  readonly createdAt?: string;
  readonly expires_at?: string | null;
  readonly expiresAt?: string | null;
};

type CursorListPayload<T> = {
  readonly items: ReadonlyArray<T>;
  readonly total?: number;
  readonly next_cursor?: string | null;
};

// ─── API Calls ───

export async function listApplications(
  params: ListApplicationsParams,
): Promise<ListApplicationsResponse> {
  const sp = new URLSearchParams();
  if (params.status) sp.set("status", params.status);
  if (params.cursor) sp.set("cursor", params.cursor);
  if (params.limit) sp.set("limit", String(params.limit));
  const qs = sp.toString();
  const res = await apiFetch<{ data: CursorListPayload<RawApplication> }>(
    qs ? `/admin/applications?${qs}` : "/admin/applications",
  );
  return {
    items: res.data.items.map(toApplication),
    total: res.data.total ?? res.data.items.length,
    next_cursor: res.data.next_cursor ?? null,
  };
}

export async function approveApplication(id: string): Promise<void> {
  await apiFetch<{ data: { approved: boolean; invite_code_id: string } }>(
    `/admin/applications/${id}/approve`,
    { method: "POST" },
  );
}

export async function rejectApplication(
  id: string,
  reason?: string,
): Promise<void> {
  await apiFetch<{ data: { rejected: boolean } }>(
    `/admin/applications/${id}/reject`,
    {
      method: "POST",
      body: JSON.stringify({ reason: reason ?? "" }),
    },
  );
}

export async function listInviteCodes(
  params: ListInviteCodesParams,
): Promise<ListInviteCodesResponse> {
  const sp = new URLSearchParams();
  if (params.issued_by) sp.set("issued_by", params.issued_by);
  if (params.status) sp.set("status", params.status);
  if (params.cursor) sp.set("cursor", params.cursor);
  if (params.limit) sp.set("limit", String(params.limit));
  const qs = sp.toString();
  const res = await apiFetch<{ data: CursorListPayload<RawInviteCode> }>(
    qs ? `/admin/invite-codes?${qs}` : "/admin/invite-codes",
  );
  return {
    items: res.data.items.map(toInviteCode),
    total: res.data.total ?? res.data.items.length,
    next_cursor: res.data.next_cursor ?? null,
  };
}

export async function createInviteCode(
  issuedBy?: string,
): Promise<AdminInviteCode> {
  const res = await apiFetch<{ data: { code: RawInviteCode } }>(
    "/admin/invite-codes",
    {
      method: "POST",
      body: JSON.stringify({ issued_by: issuedBy ?? null }),
    },
  );
  return toInviteCode(res.data.code);
}

function toApplication(raw: RawApplication): AdminApplication {
  return {
    id: raw.id,
    email: raw.email,
    name: raw.name ?? raw.displayName ?? "",
    role: raw.role,
    reason: raw.reason ?? "",
    status: toApplicationStatus(raw.status),
    rejection_reason: raw.rejection_reason ?? raw.rejectionReason ?? null,
    created_at: raw.created_at ?? raw.createdAt ?? "",
  };
}

function toApplicationStatus(status: string): ApplicationStatus {
  if (status === "approved" || status === "rejected") return status;
  return "pending";
}

function toInviteCode(raw: RawInviteCode): AdminInviteCode {
  return {
    id: raw.id,
    code: raw.code,
    issued_by: raw.issued_by ?? raw.issuedBy ?? null,
    issued_by_name: raw.issued_by_name ?? null,
    claimed_by: raw.claimed_by ?? raw.claimedBy ?? null,
    claimed_by_name: raw.claimed_by_name ?? null,
    status: toInviteCodeStatus(raw.status),
    created_at: raw.created_at ?? raw.createdAt ?? "",
    expires_at: raw.expires_at ?? raw.expiresAt ?? null,
  };
}

function toInviteCodeStatus(status: string | undefined): InviteCodeStatus {
  if (status === "claimed" || status === "expired") return status;
  return "available";
}
